import { useState, FormEvent } from 'react';

export default function HomeContactForm() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    Array.from(new FormData(form)).forEach(([key, value]) => {
      data.append(key, value as string);
    });
    try {
      await fetch('https://readdy.ai/api/form/d76johjq7u9ee9hcegs0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-[#f8f9fc]">
      <div className="max-w-screen-xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-3 gap-0 shadow-lg">
        {/* Left Steps */}
        <div className="bg-[#0b2240] px-10 py-14 flex flex-col justify-center">
          <h3 className="text-2xl font-bold text-white mb-6">Let's Get Started</h3>
          <div className="w-12 h-0.5 bg-[#9cbde5] mb-8" />
          <ol className="space-y-8">
            {[
              { step: '01', icon: 'ri-edit-2-line', text: 'Fill out the form.' },
              { step: '02', icon: 'ri-eye-line', text: 'Review your options with us.' },
              { step: '03', icon: 'ri-shield-check-line', text: 'Get the coverage you need.' },
            ].map((s) => (
              <li key={s.step} className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                  <i className={`${s.icon} text-2xl text-[#9cbde5]`} />
                </div>
                <div>
                  <span className="text-[#9cbde5] font-bold text-xs">Step {s.step}</span>
                  <p className="text-white font-semibold text-sm">{s.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        {/* Right Form */}
        <div className="lg:col-span-2 bg-white border-t-4 border-[#3072b7] px-10 py-12">
          <h3 className="text-2xl font-bold text-[#0b2240] mb-8">Contact Us</h3>

          {submitted ? (
            <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
              <div className="w-16 h-16 flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-5xl text-[#3072b7]" />
              </div>
              <h4 className="text-xl font-bold text-[#0b2240]">Message Received!</h4>
              <p className="text-gray-500 text-sm max-w-xs">
                Thank you for reaching out. Our team will get back to you shortly.
              </p>
            </div>
          ) : (
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-[#0b2240] uppercase tracking-wide">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="first_name"
                    required
                    className="border border-gray-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#3072b7] transition-colors"
                    placeholder="First name"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-[#0b2240] uppercase tracking-wide">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="last_name"
                    required
                    className="border border-gray-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#3072b7] transition-colors"
                    placeholder="Last name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-[#0b2240] uppercase tracking-wide">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="email"
                    type="email"
                    required
                    className="border border-gray-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#3072b7] transition-colors"
                    placeholder="your@email.com"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-[#0b2240] uppercase tracking-wide">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="phone"
                    type="tel"
                    required
                    className="border border-gray-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#3072b7] transition-colors"
                    placeholder="(000) 000-0000"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-[#0b2240] uppercase tracking-wide">
                  How can we help? <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="border border-gray-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#3072b7] transition-colors resize-none"
                  placeholder="Please do not include sensitive, private information in this area."
                />
                <p className="text-xs text-gray-400">Max 500 characters</p>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="bg-[#3072b7] hover:bg-[#0b2240] text-white font-bold text-sm px-10 py-3.5 rounded-md transition-colors disabled:opacity-60 whitespace-nowrap"
              >
                {submitting ? 'Sending...' : 'Submit'}
              </button>
            </form>
          )}

          <div className="mt-8 pt-6 border-t border-gray-100 text-center">
            <p className="text-sm text-gray-500">
              Don't like forms? Call us at{' '}
              <a href="tel:6013259000" className="text-[#3072b7] font-bold hover:underline">
                601-325-9000
              </a>{' '}
              or{' '}
              <a href="mailto:cameron@whiteoakig.com" className="text-[#3072b7] font-bold hover:underline">
                email us
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
